/** @format */

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'src/ui/library/styled';
import cloneDeep from 'lodash.clonedeep';
import merge from 'lodash.merge';
import isEqual from 'lodash.isequal';
import Table from './components/Table';
import TableHead from './components/TableHead';
import TableBody from './components/TableBody';
import TablePagination from './components/TablePagination';
import TableProgress from './components/TableProgress';
import { TABLE_STATUS } from 'src/constants/constants';
import { transformVerticalData } from './libs/transformTableData';
import intl from 'react-intl-universal';

import TableHeadCell from './components/TableHeadCell';
import TableRow from './components/TableRow';
import TableBodyCell from './components/TableBodyCell';

const styles = theme => ({
  root: {
    width: '100%',
    marginTop: '5px',
  },
  table: {
    overflow: 'auto',
    height: '100%',
    position: 'relative',
    maxHeight: '50rem',
    border: `solid 1px ${theme.colors.eyGrey3}`,
  },
  noTableDataMessage: {
    margin: '5rem auto',
    textAlign: 'center',
    color: theme.colors.eyGrey1,
  },
});

class TableBaseReadOnly extends React.Component {
  static propTypes = {
    /** Classname to be applied to <Table> */
    className: PropTypes.string,
    /** Styles to be applied to component */
    classes: PropTypes.object,
    /** Data that will be used to generate table columns */
    columns: PropTypes.PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayText: PropTypes.string.isRequired,
        visible: PropTypes.bool,
        sticky: PropTypes.bool,
        sortable: PropTypes.bool,
        renderComponent: PropTypes.function, // allows for custom cell rendering
      }),
    ).isRequired,
    /** Data that will be used to generate table rows */
    rows: PropTypes.array.isRequired,
    /**
     * Read Only mode will force table to ignore renderComponent per columns and display cell value.
     * It will also make rows not selectable.
     */
    readOnly: PropTypes.bool,
    /** Hide header */
    hideHeader: PropTypes.bool,
    /** Page for the table to open at */
    page: PropTypes.number,
    /** Enable/disable table pagination */
    pagination: PropTypes.bool,
    /** If provided, the total number of rows for the table */
    totalRows: PropTypes.number,
    /** If provided, the total number of rows per page */
    rowsPerPage: PropTypes.number,
    /** Server side mode means that the Table component will not handle pagination/sort. Useful for depedency on API's */
    serverSide: PropTypes.bool,
    /** Populate first column with selectable radio button */
    selectableRows: PropTypes.bool,
    /** Provide custom footer to display with the Table */
    footer: PropTypes.func,
    /** Callback function to get reference to internal setTableModel() method */
    setTableModel: PropTypes.func,
    /** Callback function to get reference to internal updateColumnValue() method */
    updateColumn: PropTypes.func,
    /** Callback function to get reference to internal cancelSelectedRows() method */
    cancelSelectedRows: PropTypes.func,
    /** Callback function to get reference to internal updateModelValue() method */
    updateModel: PropTypes.func,
    /**
     * Callback function for table updates. fn(actionType: string, tableState: object)
     * tableState enum: ('rowClick', 'cellClick', 'pageChange', 'colSort', 'rowsSelected', 'cellValueUpdate')
     */
    onTableChange: PropTypes.func,
    // Message for when no table data available
    noTableDataMsg: PropTypes.string,
  };

  static defaultProps = {
    rowsPerPage: 25,
    serverSide: false,
    pagination: true,
    selectableRows: false,
    styleOverrides: {},
    readOnly: false,
    hideHeader: false,
  };

  state = {
    order: 'asc',
    page: 0,
    orderBy: 'name',
    updateCount: 0,
    tableReady: TABLE_STATUS.initialize,
    rowCountHasChanged: false,
  };

  constructor(props) {
    super(props);
    this.tableModel = {
      columns: [],
      rows: [],
      selectedRows: [],
    };
  }

  componentDidMount() {
    this.setTableModel(this.props);

    if (this.props.setTableModel) {
      this.props.setTableModel(this.setTableModel.bind(this));
    }

    if (this.props.updateModel) {
      this.props.updateModel(this.updateModelValue.bind(this));
    }

    if (this.props.updateColumn) {
      this.props.updateColumn(this.updateColumnValue.bind(this));
    }

    if (this.props.cancelSelectedRows) {
      this.props.cancelSelectedRows(this.cancelSelectedRows.bind(this));
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.setPageState && prevProps.setPageState !== this.props.setPageState) {
      this.setState({ page: this.props.setPageState });
    } else if (this.props.resetPageToZero && prevProps.resetPageToZero !== this.props.resetPageToZero) {
      this.setState({ page: 0 });
    }

    if (prevProps.rows !== this.props.rows || prevProps.columns !== this.props.columns) {
      this.setTableModel(this.props, true);
    }
    if (prevProps.rows.length > this.props.rows.length) {
      this.setState({
        rowCountHasChanged: true,
      });
    }
    if (prevProps.rows.length < this.props.rows.length) {
      this.setState({
        rowCountHasChanged: false,
      });
    }
  }

  handleTableChange = (actionType, additionalMeta = {}, showProgress = true) => {
    if (this.props.onTableChange !== undefined) {
      if (this.props.serverSide && showProgress) {
        if (this.tableRef) {
          this.tableRef.scrollTop = 0;
        }

        this.setState({ tableReady: TABLE_STATUS.loading }, () => {
          this.props.onTableChange(actionType, {
            model: this.tableModel,
            state: this.state,
            ...additionalMeta,
          });
        });
      } else {
        this.props.onTableChange(actionType, {
          model: this.tableModel,
          state: this.state,
          ...additionalMeta,
        });
      }
    }
  };

  setUpdateComponent = (rowIndex, colIndex, fn) => {
    this.tableModel.rows[rowIndex][colIndex].updateComponent = fn;
  };

  setUpdateSelectCell = (rowIndex, fn) => {
    this.tableModel.rows[rowIndex].forEach(col => {
      col.updateSelectCell = fn;
    });
  };

  updateColumnValue = (colIndex, property, value) => {
    // only re-render if there is truly a column value change - no-op
    if (this.tableModel.columns[colIndex][property] === value) {
      return;
    }

    const newColumns = cloneDeep(this.tableModel.columns);
    newColumns[colIndex] = { ...newColumns[colIndex], [property]: value };

    this.tableModel.columns = newColumns;
    this.setState({ tableReady: TABLE_STATUS.ready });
  };

  getIndexByOrigRowIndex = origRowIndex => {
    let finalRowIndex = null;

    for (let index = 0; index < this.tableModel.rows.length; index++) {
      const row = this.tableModel.rows[index];
      if (row[0].origRowIndex === origRowIndex) {
        finalRowIndex = index;
        break;
      }
    }

    return finalRowIndex;
  };

  updateModelValue = (rowIndex, colIndex, useOrigRowIndex = false, forceCellRender = false, value) => {
    let finalRowIndex = rowIndex;
    let origRowIndex = null;

    for (let index = 0; index < this.tableModel.rows.length; index++) {
      const row = this.tableModel.rows[index];
      if (row[0].origRowIndex === rowIndex) {
        origRowIndex = index;
        break;
      }
    }

    if (useOrigRowIndex) {
      finalRowIndex = origRowIndex;
    }

    this.tableModel.rows[finalRowIndex][colIndex].value = value;

    if (forceCellRender) {
      this.tableModel.rows[finalRowIndex][colIndex].updateComponent();
    }

    const cellMeta = { rowIndex, colIndex, origRowIndex: origRowIndex, value };
    this.handleTableChange('cellValueUpdate', { cellUpdated: cellMeta }, false);
  };

  rawColumns = cols => {
    return cols.map(item => {
      if (typeof item !== 'object') return item;

      const { options, renderComponent, ...otherOpts } = item;
      return otherOpts;
    });
  };

  carrySortDirection = (curColumn, previousModelColumns) => {
    const { id } = curColumn;

    if (id === undefined) {
      return curColumn.sortDirection || null;
    }

    const targetCol = previousModelColumns.find(col => col.id === id);
    let sortDirection = null;

    if (targetCol && targetCol.sortDirection !== undefined) {
      sortDirection = targetCol.sortDirection;
    }

    if (curColumn.sortDirection) {
      sortDirection = curColumn.sortDirection;
    }

    return sortDirection;
  };

  buildColumns = newColumns => {
    const modelColumns = this.rawColumns(this.tableModel.columns);
    const propColumns = this.rawColumns(newColumns);

    if (this.tableModel.rows && this.tableModel.rows.length && isEqual(modelColumns, propColumns)) {
      return this.tableModel.columns;
    }

    const columns = newColumns.map((column, colIndex) => {
      const columnOptions = {
        sortDirection: this.carrySortDirection(column, modelColumns),
        visible: true,
        ...column,
      };

      return columnOptions;
    });
    return columns;
  };

  setTableModel = (props, TABLE_UPDATE = false) => {
    const columns = this.buildColumns(props.columns);
    const selectedRows = props.selectedRows || [];

    const rows = props.rows.map((row, rowIndex) => {
      return columns.map((col, colIndex) => {
        return {
          origRowIndex: rowIndex, // when the table gets sorted it's good to have the orig row index value
          value: row[colIndex] || '',
          updateModelValue: this.updateModelValue,
          isRowSelected: selectedRows.some(sr => {
            return sr.origRowIndex === rowIndex;
          }),
          updateSelectCell: () => {},
          isRenderComponent: col.renderComponent && props.readOnly === false ? true : false,
          renderComponent: col.renderComponent || undefined,
        };
      });
    });

    this.tableModel = { columns, rows, selectedRows };
    this.setState(prevState => ({
      tableReady: TABLE_STATUS.ready,
      updateCount: prevState.updateCount + 1,
    }));
  };

  getRowsPerPage = () => {
    const { serverSide, rows, rowsPerPage } = this.props;
    return rowsPerPage;
    //leaving for now to investigate further - AG
    //return serverSide && this.state.rowCountHasChanged === false ? rows.length : rowsPerPage;
  };

  getPaginatedModel = () => {
    const { totalRows } = this.props;
    const { page } = this.state;
    const { rows, columns } = this.tableModel;

    const rowsPerPage = this.getRowsPerPage();
    const rowCount = totalRows || rows.length;
    let startIndex = page === 0 ? 0 : page * rowsPerPage;
    const endIndex = Math.min(rowCount, (page + 1) * rowsPerPage);

    // startIndex is equal to endIndex when user is on a page other than first
    // and that page has one row and user deletes that row, then Page no. has to change.
    if (startIndex == endIndex && page > 0) {
      this.setState({ page: page - 1 });
    }

    if (this.props.serverSide) {
      startIndex = 0;
    }

    /* build paginated rows now */
    let newRows = [];

    for (let rowIndex = startIndex; rowIndex < rowCount && rowIndex < endIndex; rowIndex++) {
      if (rows[rowIndex]) newRows.push(rows[rowIndex]);
    }

    return { rows: newRows, columns };
  };

  getVisibleRows = textToSearch => {
    let visibleRows = [];
    const { rows } = (this.props.pagination && this.getPaginatedModel()) || this.tableModel;

    if (!textToSearch) {
      return rows;
    }

    const needle = textToSearch.toString();

    rows.forEach((row, rowIndex) => {
      let isRowVisible = false;

      row.forEach((col, colIndex) => {
        if (col.toString().indexOf(needle) >= 0) {
          isRowVisible = true;
        }
      });

      if (isRowVisible) {
        visibleRows.push(row);
      }
    });

    return visibleRows;
  };

  sortComparsion(order) {
    return (a, b) => {
      if (a.value === null) a.value = '';
      if (b.value === null) b.value = '';
      return (
        (typeof a.value.localeCompare === 'function' ? a.value.localeCompare(b.value) : a.value - b.value) * (order === 'asc' ? -1 : 1)
      );
    };
  }

  cancelSelectedRows = () => {
    let newTableModel = { ...this.tableModel, selectedRows: [] };

    newTableModel.rows.forEach((row, rowIndex) => {
      row.forEach(col => {
        col.isRowSelected = false;
      });
      newTableModel.rows[rowIndex][0].updateSelectCell();
    });

    this.tableModel = newTableModel;
    this.handleTableChange('rowSelected', undefined, false);
  };

  onRowSelect = selectedRow => {
    let newTableModel = { ...this.tableModel };
    const { origRowIndex } = selectedRow;
    const targetIndex = this.getIndexByOrigRowIndex(origRowIndex);
    let newSelectValue = false;

    const actionType = newTableModel.rows[targetIndex][0].isRowSelected ? 'remove' : 'add';

    if (actionType === 'add') {
      newTableModel.selectedRows.push({
        ...selectedRow,
        isRowSelected: true,
      });

      newSelectValue = true;
    } else {
      let newSelectedRows = newTableModel.selectedRows.filter(row => row.origRowIndex !== origRowIndex);
      newTableModel.selectedRows = newSelectedRows;
      newSelectValue = false;
    }

    newTableModel.rows[targetIndex].forEach(col => (col.isRowSelected = newSelectValue));
    newTableModel.rows[targetIndex][0].updateSelectCell();

    this.tableModel = newTableModel;
    this.handleTableChange('rowSelected', undefined, false);
  };

  onChangePage = page => {
    this.setState({ page }, () => {
      this.handleTableChange('changePage');
    });
  };

  onCellClick = cellMeta => {
    this.handleTableChange('cellClick', { cellClicked: cellMeta }, false);
  };

  onRowClick = rowMeta => {
    if (!this.props.isCasesDocument) {
      this.handleTableChange('rowClick', { rowClicked: rowMeta }, false);
    }
  };

  onChangeSort = (column, colIndex) => {
    if (this.props.serverSide) {
      const newDirection = column.sortDirection === 'asc' ? 'desc' : 'asc';
      this.tableModel.columns[colIndex].sortDirection = newDirection;

      this.handleTableChange('changeSort', {
        sortedBy: { ...column, sortDirection: newDirection },
      });

      return;
    }

    /*
     * When given a column to sort first extract that column and sort it.
     * Once sorted take the row positions
     */
    const { rows, columns, ...otherModelProps } = this.tableModel;

    let dataToSort = rows.map((row, sortIndex) => ({
      value: row[colIndex].value,
      sortIndex,
    }));

    column.sortDirection = column.sortDirection === 'asc' ? 'desc' : 'asc';
    dataToSort.sort(this.sortComparsion(column.sortDirection));

    const sortedRows = dataToSort.map(row => rows[row.sortIndex]);
    this.tableModel = { columns, rows: sortedRows, ...otherModelProps };

    this.setState({ tableReady: true }, () => {
      this.handleTableChange('changeSort', { sortedBy: column });
    });
  };

  getOverrideStyles = () => {
    const defaultStyles = {
      Table: {},
      TableHead: {},
      TableHeadCell: {},
      TableBody: {},
      TableBodyCell: {},
      TablePagination: {},
    };

    return merge(defaultStyles, this.props.styleOverrides);
  };

  renderVerticalRows = (columns, content) => {
    const rows = transformVerticalData(columns, content);

    const contentTable = [];
    rows.map((element, indexA) => {
      const dataRowContent = [];
      element.map((a, indexB) => {
        if (indexB === 0) {
          dataRowContent.push(
            <TableHeadCell onChangeSort={this.onChangeSort} key={indexB} colIndex={indexB} colData={a} totalColumns={element.length} />,
          );
        } else {
          dataRowContent.push(
            <TableBodyCell
              totalColumns={element.length}
              colIndex={indexB}
              rowIndex={indexA}
              rowData={a}
              cellData={a}
              onRowSelect={() => {}}
              selectableRows={() => {}}
              columnData={columns[indexB]}
              /*   styleOverrides={styleOverrides.TableBodyCell} */
              setUpdateComponent={() => {}}
              key={indexB}
            />,
          );
        }
      });
      contentTable.push(
        <TableRow key={indexA} rowType={'body'} rowIndex={indexA}>
          {dataRowContent}
        </TableRow>,
      );
    });

    return contentTable;
  };

  render() {
    const { classes, pagination, footer, selectableRows, totalRows, readOnly, hideHeader, noTableDataMsg, isCasesDocument } = this.props;
    const { page } = this.state;
    const { columns } = this.tableModel;

    const visibleRows = this.getVisibleRows();
    const rowCount = totalRows || this.tableModel.rows.length;
    const rowsPerPage = pagination && this.getRowsPerPage();

    const styleOverrides = this.getOverrideStyles();

    return (
      <div className={classes.root}>
        <div className={classes.table} ref={el => (this.tableRef = el)}>
          <Table> {this.renderVerticalRows(columns, visibleRows)}</Table>
        </div>
      </div>
    );
  }
}

export default styled(TableBaseReadOnly)(styles);
